import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { first, findIndex, last, sortBy } from 'lodash';

import { isNative } from "@dbk/ui-react-utils/src";
import { View } from 'react-native';
import { Large } from '@dbk/ui-text';
import { createContainer } from '@dbk-legacy/muster/react'
import { scaleLinear } from 'd3-scale';
import { MAX_SIMPLE_CHART_Y_AXIS_VALUE, TYPOGRAPHY } from '../../../constants';

import {
  scaleBar,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Grid,
  Chart,
  DataPoint,
} from '@dbk/ui-charts';

import {
  Circle,
  G,
  Text as SvgText,
} from '@dbk/ui-svg';

import Interactions from './interactions';
import NoDataChart from '../../common/no-data-chart';
import ResponsiveDimensionValues from '../../common/responsive-dimension-values';
import styles from './simple-chart.styles';

class SimpleBarChart extends PureComponent {
  static propTypes = {
    ...Chart.propTypes,
    bars: PropTypes.arrayOf(
      PropTypes.shape({
        titleText: PropTypes.string,
        valueText: PropTypes.string,
        value: PropTypes.number,
      })
    ),
    isTablet: PropTypes.bool,
    onInteractionChange: PropTypes.func,
    renderOuterLabel: PropTypes.func,
    selectedInteractionValue: PropTypes.string,
    margins: PropTypes.object,
    paddings: PropTypes.object,
    customBarColors: PropTypes.arrayOf(PropTypes.string),
    minHeightFactor: PropTypes.number,
    yaxislabeltext: PropTypes.string,
    xaxislabeltext: PropTypes.string,
    showTicks: PropTypes.bool,
    hasInteractions: PropTypes.bool,
    hasCustomInteractions: PropTypes.bool,
    outerLabels: PropTypes.bool,
    hasxAxis: PropTypes.bool,
    hasyAxis: PropTypes.bool,
    xFontSize: PropTypes.number,
    yFontSize: PropTypes.number,
    ylabelVerticalPadding: PropTypes.number,
    xlabelVerticalPadding: PropTypes.number,
    barMaxWidth: PropTypes.number,
    useBarMaxWidth: PropTypes.bool,
    showSelected: PropTypes.bool,
    hasGrid: PropTypes.bool,
  };

  static defaultProps = {
    bars: [],
    data: [],
    margins: { left: 50, right: 0, top: 54, bottom: 30 },
    minHeightFactor: 0.59,
    yaxislabeltext: '',
    xFontSize: TYPOGRAPHY.font.size.normal,
    yFontSize: TYPOGRAPHY.font.size.small,
    ylabelVerticalPadding: 40,
    xlabelVerticalPadding: 40,
    xTickLabelPadding: 10,
    yTickLabelPadding: 0,
    xaxislabeltext: '',
    hasInteractions: true,
    hasCustomInteractions: false,
    hasxAxis: true,
    hasyAxis: true,
    hasGrid: true,
    useBarMaxWidth: true,
    outerLabels: false,
    showSelected: false,
    renderOuterLabel: () => {
    },
  };

  getOuterComponents() {
    const {
      selectedInteractionValue,
      onInteractionChange,
      margins,
      bars,
      outerComponents,
      messages,
      hasCustomInteractions,
    } = this.props;

    const noDataMarginTop = margins && margins.top ? margins.top * -1 : 0;

    const chartOuterComponents = bars.length ?
      [<Interactions
        currency="test1"
        key="interactions"
        data={bars}
        selectedValue={selectedInteractionValue}
        onChange={onInteractionChange}
        hasCustomInteractions={hasCustomInteractions}
        messages={messages}
      />,
      ] : [<NoDataChart
        key="no-data"
        marginTop={noDataMarginTop}
        messages={messages}
      />];

    if (outerComponents) {
      return [...chartOuterComponents, ...outerComponents];
    }

    return chartOuterComponents;
  }

  render() {
    const {
      bars,
      isTablet,
      selectedInteractionValue,
      yaxislabeltext,
      xaxislabeltext,
      customBarColors,
      minHeightFactor,
      messages,
      showTicks,
      hasInteractions,
      xTickFormater,
      yTickFormater,
      xTickLabelPadding,
      yTickLabelPadding,
      xlabelVerticalPadding,
      ylabelVerticalPadding,
      xFontSize,
      yFontSize,
      onSelectedItem,
      renderOuterLabel,
      showSelected,
      barMaxWidth,
      useBarMaxWidth,
      outerLabels,
      hasxAxis,
      hasyAxis,
      hasGrid,
      paddings,
      newHeight,
      auth: { langCode },
      barColor
    } = this.props;

    const { height, width } = {
      height: ResponsiveDimensionValues.windowHeight,
      width: ResponsiveDimensionValues.windowWidth,
    };

    const data = bars.map((row, index) => ({
      ...row,
      index,
    }));
    let chartDomain = {};

    const sortedData = sortBy(data, (item) => item.value);

    const dataCount = bars.length;

    if (dataCount > 0) {
      var minVal = 0;
      let maxValue;
      if(last(sortedData).value <= 100) {
        maxValue = MAX_SIMPLE_CHART_Y_AXIS_VALUE;
      } else {
        maxValue = Math.max(last(sortedData).value + last(sortedData).value * 0.25, MAX_SIMPLE_CHART_Y_AXIS_VALUE);
      }
      // check if the min value is below Zero , then adjust the buffer of the domain chart
      if (sortedData[0].value < 0) {
        minVal = Math.min(sortedData[0].value + sortedData[0].value * 0.5, -20);
      }

      if (maxValue < 0) {
        maxValue = Math.abs(first(sortedData).value);
      }

      chartDomain = {
        min: minVal,
        max: maxValue,
      };
    }

    if (dataCount == 0) {
      return (
        <View style={styles.notice}>
          <Large tabIndex={0} role="alert" aria-label={messages['app.portfolio.dashboard.noChartData']}>{messages['app.portfolio.dashboard.noChartData']}</Large>
        </View>
      );
    }

    const customWidth = isTablet ? width * 0.68 : 500;
    const customHeight = isTablet ? height * 0.85 : 400;
    const customMinHeight = isTablet ? height * minHeightFactor : 150;
    const customMaxHeight = isTablet ? height * 0.3 : newHeight || 225;

    const animated = selectedInteractionValue != null ? false : undefined;
    const selectedIndex = findIndex(data, item => item.selected);
    const scaleBarExtraWidth = scaleLinear().domain([0, 15]).range([10, 3]);
    const formatter = new Intl.NumberFormat(langCode);

    return (
      <Chart
        axis={{
          x: {
            accessor: ({ index }) => index,
            scale: scaleBar,
          },
          y: {
            accessor: (row) => row.value > 0 ? (row.value / chartDomain.max < 0.05) ? chartDomain.max * 0.01 : row.value : row.value,
            domain: [chartDomain.min, chartDomain.max],
          },
        }}
        animated={animated}
        {...this.props}
        data={data}
        width={customWidth}
        height={customHeight}
        minHeight={customMinHeight}
        maxHeight={customMaxHeight}
        paddings={paddings}
      >
        {hasxAxis && <XAxis
          outerLabels={outerLabels}
          tickFormatter={xTickFormater ? xTickFormater : (i) => `${data[i].titleText}`}
          renderOuterLabel={renderOuterLabel}
          showTicks={false}
          showLine={false}
          fontSize={xFontSize}
          styles={{ marginTop: 20 }}
          labelVerticalPadding={xlabelVerticalPadding}
          tickLabelPadding={xTickLabelPadding}
          lineSpacing={2}
        />}

        {hasyAxis && <YAxis
          tickFormatter={yTickFormater ? yTickFormater : i => i}
          showTicks={showTicks}
          showLine={false}
          fontSize={yFontSize}
          position="start"
          labelVerticalPadding={ylabelVerticalPadding}
          tickLabelPadding={yTickLabelPadding}
          key="yaxis"
          labelText={yaxislabeltext}
        />
        }

        {hasGrid && <Grid />}

        <BarChart
          renderBar={(options) => {
            let extraProps = {};
            if (useBarMaxWidth) {
              extraProps = {
                barWidth: barMaxWidth ? barMaxWidth : options.barWidth,
              };
            }

            if (data[options.index]) {
              if (data[options.index].color) {
                extraProps = {
                  color: data[options.index].color,
                }
              }

              if (data[options.index].selected) {
                extraProps = {
                  ...extraProps,
                };

                if (useBarMaxWidth) {
                  extraProps = {
                    ...extraProps,
                    barWidth: barMaxWidth ? barMaxWidth : options.barWidth + scaleBarExtraWidth(data.length),
                  }
                }
              }
            }
            return (
              <Bar {...options} {...extraProps} lineColor="transparent" opacity={1} pivotOpacity={1} color={ extraProps.color ? extraProps.color : barColor}/>
            )
          }}
          customBarColors={customBarColors}
        />
        {(showSelected && selectedIndex !== -1) && <DataPoint index={selectedIndex}>
          <SvgText textAnchor="middle" y={isNative ? -10 : -15}
            fill="#ffffff">
            {`${formatter.format(data[selectedIndex].displayPercent || data[selectedIndex].percent)} %`}
          </SvgText>
        </DataPoint>
        }
        {hasInteractions && this.getOuterComponents()}
      </Chart>
    );
  }
}

export default createContainer({
  $queries: {
    auth: {
      langCode: true
    }
  },
})(SimpleBarChart)